import { inject, onBeforeUnmount, watch } from "vue";

/**
 * Composable that registers reactive field value(s) into the nearest export form's
 * shared model (provided via provide/inject in ExportView).
 *
 * Usage:
 *   const myVal = ref(false);
 *   useExportFormField('myFieldName', myVal);
 *
 * Or for multiple fields at once:
 *   useExportFormField({ fieldA: refA, fieldB: refB });
 */
export const EXPORT_FORM_MODEL_KEY = Symbol("exportFormModel");
export const EXPORT_ROLE_FEATURES_KEY = Symbol("exportRoleFeatures");

/**
 * @param {string | Record<string, import("vue").Ref>} nameOrMap
 * @param {import("vue").Ref} [maybeRef]
 */
export function useExportFormField(nameOrMap, maybeRef) {
  /** @type {import("vue").Ref<Record<string, any>> | null} */
  const formModel = inject(EXPORT_FORM_MODEL_KEY, null);
  if (!formModel) return;

  const entries =
    typeof nameOrMap === "string"
      ? [[nameOrMap, maybeRef]]
      : Object.entries(nameOrMap);

  for (const [name, valueRef] of entries) {
    // Set initial value
    formModel.value[name] = valueRef.value;

    // Keep in sync
    watch(valueRef, (v) => {
      formModel.value[name] = v;
    });

    onBeforeUnmount(() => {
      delete formModel.value[name];
    });
  }
}
